import { type PropsWithChildren, createContext, useContext, useEffect, useState } from "react"
import { useStore } from "zustand"
import { useShallow } from "zustand/shallow"

import type { PurchaseOrderPricing, PurchaseOrderState, createPurchaseOrderStore } from "@/stores/usePurchaseOrderStore"

const PurchaseOrderStoreContext = createContext<ReturnType<typeof createPurchaseOrderStore> | null>(null)

export function PurchaseOrderProvider({
  initialStore,
  children,
}: PropsWithChildren<{ initialStore: ReturnType<typeof createPurchaseOrderStore> }>): JSX.Element {
  const [store, setStore] = useState(initialStore)

  // Update store when initialStore changes
  useEffect(() => {
    setStore(initialStore)
  }, [initialStore])

  return <PurchaseOrderStoreContext.Provider value={store}>{children}</PurchaseOrderStoreContext.Provider>
}

// Base hook for store access
const usePurchaseOrderStore = <T,>(selector: (state: PurchaseOrderState) => T): T => {
  const store = useContext(PurchaseOrderStoreContext)
  if (!store) {
    throw new Error("usePurchaseOrderStore must be used within PurchaseOrderProvider")
  }
  return useStore(store, selector)
}

// Atomic selectors

export const useOrderLineRoundedInfo = (): Map<number, { roundingEnabled: boolean; isBagsWholeNumber?: boolean }> =>
  usePurchaseOrderStore(useShallow((state) => state.orderLineRoundedValueMap))

export const useCalculatedPricing = (): PurchaseOrderPricing =>
  usePurchaseOrderStore(useShallow((state) => state.pricing))

// Actions

export const useClearOrderLineRounded = (): PurchaseOrderState["clearOrderLineRounded"] =>
  usePurchaseOrderStore((state) => state.clearOrderLineRounded)

export const useSetOrderLineRounded = (): PurchaseOrderState["setOrderLineRounded"] =>
  usePurchaseOrderStore((state) => state.setOrderLineRounded)

export const useSetPricing = (): PurchaseOrderState["setPricing"] => usePurchaseOrderStore((state) => state.setPricing)

export const useSetPricingFetching = (): PurchaseOrderState["setPricingFetching"] =>
  usePurchaseOrderStore((state) => state.setPricingFetching)
